import React, {useContext, useEffect, useState} from 'react';
import {AuthContext} from 'contexts/auth/auth.provider';
import {useRouter} from "next/router";
import notifications from "utils/notifications";
import * as Yup from "yup";
import {useFormik} from "formik";
import BlockUi from "react-block-ui";
import {ModalContext} from "contexts/modal.provider";

function ResetInitForm({loading, errors, handleSubmit, getFieldProps}) {
    return <BlockUi tag={"div"} blocking={loading} className="">
        <div className="bg-white pb-8 px-4 sm:rounded-lg sm:px-10">
            <form onSubmit={handleSubmit}>

                <div className="sm:mx-auto sm:w-full">
                    <h3 className="my-6 text-center text-xl leading-9 font-bold text-gray-700">
                        Reset Your Password
                    </h3>
                </div>
                <div>
                    <label
                        htmlFor="email"
                        className="block text-sm font-medium leading-5 text-gray-700"
                    >
                        Please enter your email address below.
                    </label>
                    <div className="mt-1 rounded-md shadow-sm">
                        <input
                            {...getFieldProps('email')}
                            id="email"
                            type="email"
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        />
                    </div>
                    {errors.email && errors.email &&
                    <p className="mt-2 text-sm text-red-600" id="email-error">{errors.email}</p>}
                </div>

                <div className="mt-6">
                          <span className="block w-full rounded-md shadow-sm">
                            <button
                                type="submit"
                                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-bold rounded-md text-white bg-scuttle-blue  focus:outline-none  transition duration-150 ease-in-out"
                            >
                              Submit
                            </button>
                          </span>
                </div>
            </form>

        </div>
    </BlockUi>
}


export default function ForgotPassModal() {
    const {closeModal} = useContext(ModalContext);
    const {resetPassword, loading} = useContext<any>(AuthContext);
    const [sent, setSent] = useState(false);

    const handleReset = ({email}) => {
        resetPassword(email)
            .then(() => {
                notifications.success(`Please check your email`, `Success`);
                setSent(true);
            }).catch((e) => {
            notifications.error(e.message);
        });
    };

    function getSchema() {
        return Yup.object().shape({
            email: Yup.string()
                .email('Invalid email')
                .required('Email is required'),
        });
    }

    const {getFieldProps, handleSubmit, errors} = useFormik({
        initialValues: {
            email: '',
        },
        onSubmit: handleReset,
        validationSchema: getSchema()
    });


    return (
        <div
            className="flex flex-col justify-center">

            {!sent && <ResetInitForm
                loading={loading}
                errors={errors}
                getFieldProps={getFieldProps}
                handleSubmit={handleSubmit}/>
            }
            {sent && <div
                className="bg-white rounded-lg px-4 pt-5 pb-8 overflow-hidden transform transition-all mt-8 sm:mx-auto sm:w-full sm:max-w-md "
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-headline"
            >
                <div>
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-scuttle-orange">
                        <svg
                            className="h-6 w-6 text-green-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                        <h3
                            className="text-lg leading-6 font-medium text-gray-900"
                            id="modal-headline"
                        >
                            Thank you, your request has been submitted. <br/> Please check your email.
                        </h3>
                        <div className="mt-2">
                            <p className="text-sm leading-5 text-gray-500">
                                We've sent an email with further instructions.
                            </p>
                        </div>
                        <div className="mt-6">
                          <span className="block w-full rounded-md shadow-sm">
                            <button
                                onClick={closeModal}
                                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-bold rounded-md text-white bg-scuttle-blue transition duration-150 ease-in-out"
                            >
                              Close
                            </button>
                          </span>
                        </div>
                    </div>
                </div>

            </div>}

        </div>
    );
}

export function MigratePassModal({initEmail}) {
    const {closeModal} = useContext(ModalContext);
    const {resetPassword, loading} = useContext<any>(AuthContext);
    const [sent, setSent] = useState(false);

    const handleReset = ({email}) => {
        resetPassword(email)
            .then(() => {
                setSent(true);
            }).catch((e) => {
            notifications.error(e.message);
        });
    };

    useEffect(() => {
        if (initEmail) handleReset({email: initEmail})
    }, [initEmail])

    return (
        <div
            className="flex flex-col justify-center">

            <div
                className="bg-white rounded-lg px-4 pb-8 overflow-hidden transform transition-all mt-4 sm:mx-auto sm:w-full sm:max-w-md "
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-headline"
            >
                <div>
                    <div className="mt-3 text-center">
                        <h3
                            className="text-lg leading-6 font-medium text-gray-900"
                            id="modal-headline"
                        >
                            We've redesigned our store for you!
                        </h3>
                        <div className="mt-6">
                            <p className="mt-2 text-base leading-6 text-gray-500">
                                In order to activate your account for our new store, please follow the instructions
                                we’ve emailed to you. It will take less than a minute!
                            </p>
                            <p className="mt-2 text-base leading-6 text-gray-500">
                                You are welcome to complete your booking as a guest if you prefer.
                            </p>

                            <p className="mt-2 text-base leading-6 text-gray-500">
                                Thank you for your support so far!
                            </p>
                        </div>
                        <div className="mt-6">
                          <span className="block w-full rounded-md shadow-sm">
                            <button
                                onClick={closeModal}
                                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-bold rounded-md text-white bg-scuttle-blue transition duration-150 ease-in-out"
                            >
                              Close
                            </button>
                          </span>
                        </div>
                    </div>
                </div>

            </div>

        </div>
    );
}
